import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Clock, MapPin } from "lucide-react";
import { COMPANY } from "../data/mock";

export default function AuthLayout({
  variant = "customer", // 'customer' | 'driver' | 'admin'
  title,
  subtitle,
  children,
  footer
}) {
  const accentColor = variant === "admin" ? "#0f2847" : "#f59321";
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      {/* Brand panel */}
      <aside className="hidden lg:flex lg:w-[44%] xl:w-[40%] bg-[#0f2847] text-white relative overflow-hidden flex-col p-12 stripe-bg">
        <Link to="/" className="flex items-center gap-3 group relative z-10">
          <img
            src={COMPANY.logo}
            alt="518 Courier"
            className="w-12 h-12 rounded-full bg-white"
          />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-white text-lg">
              518 <span style={{ color: accentColor }}>Courier</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
              Albany · Capital Region
            </div>
          </div>
        </Link>

        <div className="flex-1 flex flex-col justify-center relative z-10 max-w-md">
          <div
            className="inline-flex w-fit items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold mb-5"
            style={{ color: accentColor }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor }} />
            {variant === "driver" ? "Driver portal" : variant === "admin" ? "Admin portal" : "Customer portal"}
          </div>
          <h2 className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.1]">
            {variant === "driver"
              ? "Drive with 518. Get paid on time."
              : variant === "admin"
              ? "Run dispatch from anywhere."
              : "Welcome back to the fastest fleet in the 518."}
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            {variant === "driver"
              ? "Manage today's routes, log mileage, and track your earnings — all from one screen."
              : variant === "admin"
              ? "Dispatch drivers, monitor live deliveries, and review revenue in real-time."
              : "Track every delivery, review invoices and re-order routine pickups in two clicks."}
          </p>

          <ul className="mt-10 space-y-4">
            {[
              { Icon: ShieldCheck, text: "Bank-grade encryption on every session" },
              { Icon: Clock, text: "24 / 7 live human dispatch" },
              { Icon: MapPin, text: "Local team based in Albany, NY" }
            ].map(({ Icon, text }, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-200">
                <span className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center">
                  <Icon className="w-4 h-4" style={{ color: accentColor }} />
                </span>
                <span className="text-sm">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-xs text-slate-400 relative z-10">
          © {new Date().getFullYear()} OBLED Corporation d/b/a 518 Courier ·{" "}
          <Link to="/conditions" className="hover:text-white">Conditions</Link>
        </div>
      </aside>

      {/* Form panel */}
      <section className="flex-1 flex flex-col">
        <header className="flex items-center justify-between px-6 lg:px-12 h-20 border-b border-slate-100">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0f2847]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <img
              src={COMPANY.logo}
              alt="518 Courier"
              className="w-9 h-9 rounded-full"
            />
            <span className="font-display font-extrabold text-[#0f2847]">
              518 <span style={{ color: accentColor }}>Courier</span>
            </span>
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0f2847] leading-tight">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-slate-600">{subtitle}</p>
              )}
            </div>
            {children}
            {footer && <div className="mt-8 text-sm text-slate-600 text-center">{footer}</div>}
          </div>
        </div>
      </section>
    </div>
  );
}
